import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PopupList = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [popupToDelete, setPopupToDelete] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/list-popup`);
      
      // Handle case when API returns "No popups found" message
      if (response.data.message === "No popups found.") {
        setPopups([]);
        setError(null);
        return;
      }
      
      // Handle case when popups array exists
      if (response.data.popups) {
        const transformedPopups = response.data.popups.map(popup => ({
          ...popup,
          content: popup.description,
        }));
        setPopups(transformedPopups);
        setError(null);
        return;
      }

      // If response format is unexpected
      setError('Unexpected response format from server');
      setPopups([]);
      
    } catch (err) {
      // Handle network errors or server errors
      const errorMessage = err.response?.data?.error || 
                         err.response?.data?.message || 
                         'Failed to fetch popups';
      setError(errorMessage);
      setPopups([]);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (popup) => {
    setPopupToDelete(popup);
    setDeleteModalOpen(true);
  };

  const handleDeletePopup = async () => {
    if (!popupToDelete) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/delete-popup/${popupToDelete.id}`);
      
      setPopups(popups.filter(popup => popup.id !== popupToDelete.id));
      toast.success('Popup deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete popup');
    } finally {
      setDeleteModalOpen(false);
      setPopupToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-3">Current Popups</h3>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error && error !== "No popups found.") {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-3">Current Popups</h3>
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
          <button 
            onClick={fetchPopups} 
            className="ml-2 text-blue-600 hover:text-blue-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium mb-3">Current Popups</h3>
      
      {popups.length === 0 ? (
        <div className="p-4 bg-gray-100 text-gray-700 rounded-md text-center">
          No popups found. Create your first popup!
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {popups.map((popup) => (
              <div key={popup.id} className="border rounded-lg p-4">
                <div className="mb-3">
                  <img
                    src={popup.image}
                    alt={popup.title}
                    className="rounded-lg w-full h-40 object-cover"
                  />
                </div>
                <h4 className="font-medium">{popup.title}</h4>
                <p className="text-gray-600 text-sm mb-2">{popup.content}</p>
                <p className="text-indigo-600 text-sm font-semibold">
                  {popup.catchphrase}
                </p>
                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center">
                    <span
                      className={`inline-block w-3 h-3 rounded-full mr-2 ${
                        popup.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    ></span>
                    <span className="text-sm">
                      {popup.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => confirmDelete(popup)}
                    className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Delete Confirmation Modal */}
          {deleteModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Confirm Deletion</h3>
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    &times;
                  </button>
                </div>
                <p className="mb-4">
                  Are you sure you want to delete the popup "{popupToDelete?.title}"?
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeletePopup}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PopupList;